import type {HistoriaDeFinanciacion} from '@cnc/contracts';
import {useState} from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {useHistoriaDeFinanciacion} from '../api/financiacion';
import {AYUDA_FINANCIACIONES as A} from './ayuda-exportaciones';
import {NotaDeAyuda} from './nota-de-ayuda';
import {localeActual, tr} from '../i18n/idioma';
import {fecha, numero, usd} from '../utilidades/formato';

// Colores fijos por linea (la misma linea siempre con el mismo color).
const COLOR: Record<string, string> = {
  BLQ: '#0f7a5c',
  BLQM: '#6cbfa3',
  LC: '#a8812e',
  BC: '#4f6d8f',
  HG: '#8a8f98',
};
const COLOR_CUPO = '#b3261e';
const colorDe = (codigo: string) => COLOR[codigo] ?? '#6f7c7f';

const RANGOS = [
  {clave: '12', meses: 12},
  {clave: '24', meses: 24},
  {clave: 'todo', meses: null},
] as const;

// Uso de cada cupo en el tiempo (Daniel, 9/10/2026): cuanto dinero tenia
// afuera cada financiador semana a semana, el maximo al que llego, como va hoy
// y la proyeccion con las fechas esperadas del seguimiento.
export function HistoriaDeCupos() {
  const consulta = useHistoriaDeFinanciacion();
  const [rango, setRango] = useState<(typeof RANGOS)[number]['clave']>('24');
  const h = consulta.data;
  if (consulta.isError) return <p className="text-sm text-alerta">{consulta.error.message}</p>;
  if (!h) return null;
  const meses = RANGOS.find(r => r.clave === rango)!.meses;
  const desde = meses
    ? (() => {
        const d = new Date(`${h.hoy}T00:00:00Z`);
        d.setUTCMonth(d.getUTCMonth() - meses);
        return d.toISOString().slice(0, 10);
      })()
    : '';
  const puntos = h.puntos.filter(p => p.fecha >= desde);
  const grupos = h.grupos.filter(g => h.puntos.some(p => [g.codigo, ...g.incluye].some(l => (p.porLinea[l] ?? 0) > 0)));
  return (
    <section className="space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="historia-de-cupos">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Uso de los cupos en el tiempo', 'Credit line use over time')}</h2>
          <p className="text-xs text-apagado">
            {tr(
              'Cuánto dinero tenía afuera cada financiador cada semana: desde el desembolso (o el zarpe) hasta que el comprador pagó. Después de hoy, la proyección con las fechas esperadas del seguimiento, como si se financiara todo lo que viene. La venta directa muestra lo que Hindustan le debe a la empresa del grupo.',
              'How much money each financer had out each week: from the disbursement (or departure) until the buyer paid. After today, the projection with the expected tracking dates, as if everything coming were financed. Direct sale shows what Hindustan owes the group company.',
            )}
          </p>
        </div>
        <div className="flex gap-1 text-sm" role="group" aria-label={tr('Periodo', 'Period')}>
          {RANGOS.map(r => (
            <button
              key={r.clave}
              type="button"
              aria-pressed={rango === r.clave}
              onClick={() => setRango(r.clave)}
              className={`rounded-md border px-2 py-1 ${rango === r.clave ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white hover:bg-papel'}`}
            >
              {r.meses ? tr(`${r.meses} meses`, `${r.meses} months`) : tr('Todo', 'All')}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {grupos.map(g => (
          <GraficaDeCupo key={g.codigo} grupo={g} puntos={puntos} hoy={h.hoy} />
        ))}
      </div>
    </section>
  );
}

function GraficaDeCupo({
  grupo,
  puntos,
  hoy,
}: {
  grupo: HistoriaDeFinanciacion['grupos'][number];
  puntos: HistoriaDeFinanciacion['puntos'];
  hoy: string;
}) {
  const lineas = [grupo.codigo, ...grupo.incluye];
  const datos = puntos.map(p => ({fecha: p.fecha, ...Object.fromEntries(lineas.map(l => [l, p.porLinea[l] ?? 0]))}));
  const corto = new Intl.NumberFormat(localeActual(), {notation: 'compact', maximumFractionDigits: 1});
  const mesCorto = new Intl.DateTimeFormat(localeActual(), {month: 'short', year: '2-digit', timeZone: 'UTC'});
  const ultimo = datos.at(-1)?.fecha;
  const visibleMaximo = grupo.maximo && datos.some(d => d.fecha <= grupo.maximo!.fecha) ? grupo.maximo : null;
  const nombre = grupo.financia ? grupo.nombre : tr('Venta directa (por cobrar)', 'Direct sale (receivable)');
  const pct = (v: number) => (grupo.cupoUsd ? ` · ${numero((v / grupo.cupoUsd) * 100)} %` : '');
  return (
    <div className="rounded-lg border border-linea p-3" data-prueba={`historia-${grupo.codigo}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-medium text-verde-oscuro">
          {nombre}
          {grupo.incluye.length > 0 && (
            <span className="ml-1 text-xs font-normal text-apagado">
              {tr(`(incluye ${grupo.incluye.join(', ')})`, `(includes ${grupo.incluye.join(', ')})`)}
            </span>
          )}
        </div>
        {grupo.cupoUsd !== null && (
          <span className="text-xs text-apagado">{tr(`Cupo ${usd(grupo.cupoUsd)}`, `Line ${usd(grupo.cupoUsd)}`)}</span>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-apagado">
            {tr('Hoy', 'Today')}
            <NotaDeAyuda texto={A.historiaHoy} />
          </dt>
          <dd className="text-base font-semibold tabular-nums">{usd(grupo.hoyUsd)}</dd>
          <dd className="text-apagado">{pct(grupo.hoyUsd).replace(' · ', '')}</dd>
        </div>
        <div>
          <dt className="text-apagado">
            {tr('Máximo alcanzado', 'Highest reached')}
            <NotaDeAyuda texto={A.historiaMaximo} />
          </dt>
          <dd className="text-base font-semibold tabular-nums">{grupo.maximo ? usd(grupo.maximo.usd) : '—'}</dd>
          <dd className="text-apagado">{grupo.maximo ? `${fecha(grupo.maximo.fecha)}${pct(grupo.maximo.usd)}` : ''}</dd>
        </div>
        <div>
          <dt className="text-apagado">
            {tr('Máximo proyectado', 'Highest projected')}
            <NotaDeAyuda texto={A.historiaProyectado} />
          </dt>
          <dd
            className={`text-base font-semibold tabular-nums ${
              grupo.cupoUsd && grupo.maximoProyectado && grupo.maximoProyectado.usd > grupo.cupoUsd ? 'text-alerta' : ''
            }`}
          >
            {grupo.maximoProyectado ? usd(grupo.maximoProyectado.usd) : '—'}
          </dd>
          <dd className="text-apagado">
            {grupo.maximoProyectado ? `${fecha(grupo.maximoProyectado.fecha)}${pct(grupo.maximoProyectado.usd)}` : ''}
          </dd>
        </div>
      </dl>
      <div className="mt-2 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={datos} margin={{top: 12, right: 16, left: 0, bottom: 0}}>
            <CartesianGrid stroke="var(--color-linea)" vertical={false} />
            <XAxis
              dataKey="fecha"
              tick={{fontSize: 11, fill: 'var(--color-apagado)'}}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
              tickFormatter={v => mesCorto.format(new Date(`${String(v)}T00:00:00Z`))}
            />
            <YAxis
              tick={{fontSize: 11, fill: 'var(--color-apagado)'}}
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={v => (typeof v === 'number' ? corto.format(v) : '')}
              domain={[0, (max: number) => Math.max(max, grupo.cupoUsd ?? 0) * 1.05]}
            />
            <Tooltip
              labelFormatter={v =>
                `${tr('Semana del', 'Week of')} ${fecha(String(v))}${String(v) > hoy ? tr(' (proyección)', ' (projection)') : ''}`
              }
              formatter={v => (typeof v === 'number' ? usd(v) : '—')}
              contentStyle={{background: 'var(--color-white)', borderColor: 'var(--color-linea)'}}
            />
            {lineas.length > 1 && <Legend />}
            {ultimo && ultimo > hoy && (
              <ReferenceArea x1={hoy} x2={ultimo} fill="var(--color-papel)" fillOpacity={0.8} ifOverflow="hidden" />
            )}
            {lineas.map(l => (
              <Area
                key={l}
                type="stepAfter"
                dataKey={l}
                name={l}
                stackId="uso"
                stroke={colorDe(l)}
                fill={colorDe(l)}
                fillOpacity={0.35}
                isAnimationActive={false}
              />
            ))}
            {grupo.cupoUsd !== null && (
              <ReferenceLine
                y={grupo.cupoUsd}
                stroke={COLOR_CUPO}
                strokeDasharray="6 4"
                label={{value: tr('Cupo', 'Line'), position: 'insideTopLeft', fill: COLOR_CUPO, fontSize: 11}}
              />
            )}
            <ReferenceLine
              x={hoy}
              stroke="var(--color-tinta)"
              strokeDasharray="2 3"
              label={{value: tr('Hoy', 'Today'), position: 'top', fill: 'var(--color-apagado)', fontSize: 11}}
            />
            {visibleMaximo && (
              <ReferenceDot
                x={datos.reduce((m, d) => (d.fecha <= visibleMaximo.fecha ? d.fecha : m), datos[0]!.fecha)}
                y={visibleMaximo.usd}
                r={4}
                fill={COLOR_CUPO}
                stroke="white"
                label={{value: tr('Máximo', 'Highest'), position: 'top', fill: COLOR_CUPO, fontSize: 11}}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {(grupo.sinFechas > 0 || grupo.estimadas > 0) && (
        <p className="mt-1 text-xs text-apagado">
          {grupo.sinFechas > 0 &&
            tr(
              `${numero(grupo.sinFechas)} exportaciones sin fechas o sin monto no se pueden ubicar en el tiempo. `,
              `${numero(grupo.sinFechas)} exports without dates or amount cannot be placed in time. `,
            )}
          {grupo.estimadas > 0 &&
            tr(
              `${numero(grupo.estimadas)} sin fecha de pago: se estimó zarpe + 195 días, como el Status BLQ.`,
              `${numero(grupo.estimadas)} without payment date: estimated as departure + 195 days, like the Status BLQ.`,
            )}
        </p>
      )}
    </div>
  );
}
